<template>
    <div>
        <div class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1 class="m-0 text-capitalize">Players</h1>
                    </div>
                    <div class="col-sm-6">
                        <ol class="breadcrumb float-sm-right">
                            <li class="breadcrumb-item">
                                <router-link to="/">Home</router-link>
                            </li>
                            <li class="breadcrumb-item">Reports</li>
                            <li class="breadcrumb-item active text-capitalize">
                                <i class="fas fa-list"></i> Players
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="container-fluid">
                <data-table-wrapper
                    ref="dataTable"
                    :columns="columns"
                    :filters="filters"
                    :filter-defaults="filterDefaults"
                    :on-data-load="onDataLoad"
                    :show-filters="true"
                    disable-primary-column="true"
                    disable-edit-action="true"
                    disable-delete-action="true"
                    endpoint="/players"
                    api-service="analytics"
                    sort-column-default="roundsCount"
                    sort-order-default="DESC"
                >
                    <template v-slot:filter-buttons>
                        <button class="btn btn-default float-right" @click="doExport">
                            <span v-if="!exportInProgress">
                                <i class="fa fa-cloud-download"></i> {{ $t('Export') }}
                            </span>
                            <span v-else>
                                <i class="fas fa-spinner fa-pulse"></i> {{ $t('Export') }}
                            </span>
                        </button>
                    </template>
                </data-table-wrapper>
            </div>
        </section>
    </div>
</template>

<script>
import * as formatUtils from '../helpers/formatUtils';
import * as dateUtils from '../helpers/dateUtils';
import DataTableWrapper from '../components/DataTableWrapper';
import {mapGetters} from 'vuex';

export default {
    components: {
        DataTableWrapper,
    },
    data() {
        return {
            exportInProgress: false,
            appliedPeriod: {fromDate: null, toDate: null, gameIds: []},
            filters: [
                {
                    field: 'fromDate',
                    placeholder: 'From Date',
                    label: 'From Date',
                    type: 'date-picker',
                    shortcuts: dateUtils.getFilterShortcuts()
                },
                {
                    field: 'toDate',
                    placeholder: 'To Date',
                    label: 'To Date',
                    type: 'date-picker',
                    shortcuts: dateUtils.getFilterShortcuts()
                },
                {
                    field: 'partnerIds',
                    placeholder: 'Select partners',
                    label: 'Partners',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (partners) => { return partners ? partners.map(partner => partner.id) : [] },
                    getOptions: () => this.getPartners,
                    trackBy: 'id'
                },
                {
                    field: 'operatorIds',
                    placeholder: 'Select operators',
                    label: 'Operators',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (operators) => { return operators ? operators.map(operator => operator.id) : [] },
                    getOptions: () => this.getOperatorsOptions,
                    trackBy: 'id'
                },
                {
                    field: 'gameIds',
                    placeholder: 'Select games',
                    label: 'Games',
                    type: 'multiselect',
                    optionLabel: 'name',
                    multiple: true,
                    getValue: (games) => { return games ? games.map(game => game.id) : [] },
                    getOptions: () => this.getGames,
                    trackBy: 'id'
                },
                {
                    field: 'uid',
                    placeholder: 'User ID',
                    label: 'User ID',
                },
                {
                    field: 'username',
                    placeholder: 'Username',
                    label: 'Username',
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'getGames',
            'getPartners',
            'getPartner',
            'getOperators',
            'getOperator',
            'getCurrency',
        ]),
        getOperatorsOptions() {
            return this.getOperators.filter((operator) => {
                let selectedPartnerIds = this.$refs.dataTable.getFilterValue('partnerIds');
                if (selectedPartnerIds.length === 0) {
                    return true;
                }

                if (!operator.partnerId) {
                    return false;
                }

                return selectedPartnerIds.includes(operator.partnerId);
            });
        },
        filterDefaults() {
            return {
                fromDate: dateUtils.getTodayDate()
            };
        },
        columns() {
            return [
                {
                    field: 'uid',
                    title: 'User ID',
                    sorting: true,
                    callback: ({row}) => {
                        return (
                            <router-link to={{ name: 'transactions', query: this.getTransactionsQuery(row) }}>
                                { row.uid }
                            </router-link>
                        );
                    },
                },
                {
                    field: 'partnerId',
                    title: 'Partner',
                    sorting: false,
                    callback: ({row}) => {
                        let partner = this.getPartner(String(row.partnerId));
                        return partner ? partner.name : row.partnerId;
                    },
                },
                {
                    field: 'operatorId',
                    title: 'Operator',
                    sorting: false,
                    callback: ({row}) => {
                        let operator = this.getOperator(String(row.operatorId));
                        return operator ? operator.name : row.operatorId;
                    },
                },
                {
                    field: 'roundsCount',
                    title: 'Rounds count',
                    sorting: true,
                },
                {
                    field: 'currency',
                    title: 'Currency',
                    sorting: false,
                },
                {
                    field: 'totalBet',
                    title: 'Total bet',
                    sorting: true,
                    callback: ({row}) => formatUtils.formatNumberToFixed(row.totalBet),
                },
                {
                    field: 'totalWin',
                    title: 'Total win',
                    sorting: true,
                    callback: ({row}) => formatUtils.formatNumberToFixed(row.totalWin),
                },
                {
                    field: 'ggr',
                    title: 'GGR',
                    sorting: true,
                    callback: ({row}) => formatUtils.formatNumberToFixed(row.ggr),
                },
                {
                    field: 'rateEur',
                    title: 'Rate EUR',
                    sorting: false,
                    callback: ({row}) => this.formatInEur(row.currency, 1),
                },
                {
                    field: 'totalBetEur',
                    title: 'Total bet EUR',
                    sorting: false,
                    callback: ({row}) => this.formatInEur(row.currency, row.totalBet),
                },
                {
                    field: 'totalWinEur',
                    title: 'Total win EUR',
                    sorting: false,
                    callback: ({row}) => this.formatInEur(row.currency, row.totalWin),
                },
                {
                    field: 'ggrEur',
                    title: 'GGR EUR',
                    sorting: false,
                    callback: ({row}) => this.formatInEur(row.currency, row.ggr),
                },
            ];
        },
    },
    methods: {
        onDataLoad(dataTable) {
            let query = dataTable.getPageQuery();

            this.appliedPeriod = {
                fromDate: query.fromDate || null,
                toDate: query.toDate || null,
                gameIds: query.gameIds || [],
            };
        },
        formatInEur(currencyCode, amount) {
            let currency = this.getCurrency(currencyCode);
            if (currency === undefined) {
                return 'n/a';
            }

            return formatUtils.formatNumberToFixed(amount * currency.rate);
        },
        getTransactionsQuery(row) {
            return {
                uid: row.uid,
                fromDate: this.appliedPeriod.fromDate,
                toDate: this.appliedPeriod.toDate,
                gameIds: this.appliedPeriod.gameIds,
                partnerIds: [String(row.partnerId)],
                operatorIds: [String(row.operatorId)],
                currency: row.currency,
            };
        },
        doExport() {
            if (this.exportInProgress) {
                return;
            }
            this.exportInProgress = true;
            this.$exportService.doExport('players', this.$refs.dataTable.getPageQuery()).finally(() => {
                this.exportInProgress = false;
            });
        },
    },
};
</script>
